import React, { createContext, useContext, useState, useEffect } from 'react';
import { PublicClientApplication, InteractionStatus } from '@azure/msal-browser';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { msalConfig, loginRequest, tokenRequest } from '../config/authConfig';

const AuthContext = createContext();

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthContextProvider>
        {children}
      </AuthContextProvider>
    </MsalProvider>
  );
};

const AuthContextProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(null);

  // Listen for force logout events from API interceptor
  useEffect(() => {
    const handleForceLogout = (event) => {
      
      logout();
    };

    window.addEventListener('forceLogout', handleForceLogout);
    
    return () => {
      window.removeEventListener('forceLogout', handleForceLogout);
    };
  }, []);

  // Initialize user data when authenticated
  useEffect(() => {
    const initializeUser = async () => {
      try {
        // Handle redirect result first
        const redirectResponse = await instance.handleRedirectPromise();
        if (redirectResponse) {
          console.log('Redirect login successful:', redirectResponse);
        }

        if (isAuthenticated && accounts.length > 0) {
          const account = accounts[0];
          setUser({
            id: account.localAccountId,
            name: account.name || account.username,
            displayName: account.name,
            email: account.username,
            username: account.username
          });

          // Get access token
          try {
            const tokenResponse = await instance.acquireTokenSilent({
              ...tokenRequest,
              account: account
            });
            setAccessToken(tokenResponse.accessToken);
            localStorage.setItem('msal_access_token', tokenResponse.accessToken);
          } catch (error) {
            console.warn('Token acquisition failed:', error);
          }
        }
      } catch (error) {
        console.error('Authentication initialization failed:', error);
        localStorage.removeItem('msal_access_token');
      }
      setLoading(false);
    };

    if (inProgress === InteractionStatus.None) {
      initializeUser();
    }
  }, [isAuthenticated, accounts, instance, inProgress]);

  const login = async () => {
    try {
      setLoading(true);
      // Use loginRedirect instead of loginPopup
      await instance.loginRedirect(loginRequest);
      // Note: This function won't return normally since it redirects
    } catch (error) {
      console.error('Login failed:', error);
      setLoading(false);
      throw error;
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      
      // Clear local storage and state
      localStorage.removeItem('msal_access_token');
      setUser(null);
      setAccessToken(null);
      
      // Use logoutRedirect for clean logout
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout failed:', error);
      // Force reload if logout fails
      window.location.reload();
    } finally {
      setLoading(false);
    }
  };

  const getAccessToken = async () => {
    if (!isAuthenticated || accounts.length === 0) {
      return null;
    }

    try {
      const tokenResponse = await instance.acquireTokenSilent({
        ...tokenRequest,
        account: accounts[0],
      });
      setAccessToken(tokenResponse.accessToken);
      localStorage.setItem('msal_access_token', tokenResponse.accessToken);
      return tokenResponse.accessToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      // If silent token acquisition fails, try interactive
      try {
        const tokenResponse = await instance.acquireTokenPopup(tokenRequest);
        setAccessToken(tokenResponse.accessToken);
        localStorage.setItem('msal_access_token', tokenResponse.accessToken);
        return tokenResponse.accessToken;
      } catch (interactiveError) {
        console.error('Interactive token acquisition failed:', interactiveError);
        return null;
      }
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    accessToken,
    login,
    logout,
    getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};